<template>
  <Section
    :id="id"
    class="quick-quote-stop-section"
    :header="t('shipments.stops', 2)"
  >
    <ul role="list" class="quick-quote-stop-section-list">
      <li>
        <QuickQuoteStopEdit
          id="origin"
          v-model="internalOrigin"
          :has-attempted-resolution="hasAttemptedResolutionOrigin"
        />
      </li>
      <li>
        <QuickQuoteStopEdit
          id="destination"
          v-model="internalDestination"
          :has-attempted-resolution="hasAttemptedResolutionDestination"
        />
      </li>
    </ul>
  </Section>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { Ref } from 'vue';
import { ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { QuickQuoteShippingPoint } from '@/model';

import QuickQuoteStopEdit from '@/components/quickQuote/QuickQuoteStopEdit.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  origin: {
    type: QuickQuoteShippingPoint,
    default: () => new QuickQuoteShippingPoint(),
  },
  destination: {
    type: QuickQuoteShippingPoint,
    default: () => new QuickQuoteShippingPoint(),
  },
  hasAttemptedResolutionOrigin: {
    type: Boolean,
    default: false,
  },
  hasAttemptedResolutionDestination: {
    type: Boolean,
    default: false,
  },
});
const {
  id,
  origin,
  destination,
  hasAttemptedResolutionOrigin,
  hasAttemptedResolutionDestination,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'update:origin', modelValue: QuickQuoteShippingPoint): void;
  (event: 'update:destination', modelValue: QuickQuoteShippingPoint): void;
}>();

const { t } = useI18n();

const internalOrigin: Ref<QuickQuoteShippingPoint> = ref(
  new QuickQuoteShippingPoint(origin.value),
);
const internalDestination: Ref<QuickQuoteShippingPoint> = ref(
  new QuickQuoteShippingPoint(destination.value),
);

watch(
  origin,
  (newVal) => {
    internalOrigin.value = new QuickQuoteShippingPoint(newVal);
  },
  { deep: true, immediate: true },
);

watch(
  internalOrigin,
  (newVal) => {
    if (!isEqual(origin.value, newVal)) {
      emit('update:origin', newVal);
    }
  },
  { deep: true },
);

watch(
  destination,
  (newVal) => {
    internalDestination.value = new QuickQuoteShippingPoint(newVal);
  },
  { deep: true, immediate: true },
);

watch(
  internalDestination,
  (newVal) => {
    if (!isEqual(destination.value, newVal)) {
      emit('update:destination', newVal);
    }
  },
  { deep: true },
);
</script>

<style>
.quick-quote-stop-section {
  .section-data {
    display: flex;
    gap: var(--gap-1);
    flex-direction: column;
    align-items: start;

    .quick-quote-stop-section-list {
      display: flex;
      flex-direction: column;
      gap: var(--gap-1);
      width: 100%;
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }
}
</style>
